import { t } from "i18next";
import TextInput from "../../components/TextInput/TextInput";
import Dropdown from "../../components/Dropdown/Dropdown";
import usStates from "../usStates.json";
import { useMemo, useRef, useEffect, useState } from "react";
import { ReactComponent as InfoLogo } from "../../../assets/icons/svg/info-icon.svg";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Popper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector, useAppDispatch } from "../../../hooks/redux-hooks/hooks";
import { update } from "../../../features/formDataSlice";
import { Button, Col, Container, Row, Form } from "react-bootstrap";
import TagManager from "react-gtm-module";
import { zipMask, digitsOnly } from "../../../utils/string-utils";

interface PersonalDataFormProps {
  canContinue: boolean;
}
export const PersonalDataForm = ({
  canContinue,
}: PersonalDataFormProps) => {

  const formState = useAppSelector((state) => state.formData.value);
  const dispatch = useAppDispatch();

  const isPrimaryAccountHolder = useMemo(
    () => Boolean(formState.primaryAccountHolder),
    [formState.primaryAccountHolder]
  );

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | Event) => {
      if (
        anchorRef.current &&
        "contains" in anchorRef.current &&
        !anchorRef.current.contains(event.target as Node) &&
        anchorEl &&
        "contains" in anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      window.addEventListener("click", handleClickOutside);
    }

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [open, anchorEl]);

  const handleButtonClick = () => {
    setOpen(true);
    setAnchorEl(anchorRef.current);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const id = open ? "popper-id" : undefined;

  useEffect(() => {
    if (bottomRef.current) {
      window.scrollTo({
        top: bottomRef.current.offsetTop,
        behavior: "smooth",
      });
    }

    TagManager.dataLayer({
      dataLayer: {
        event: "porting_event",
        event_name: "personal_data_start",
        form_name: "Porting",
        form_step: "personal data",
        form_step_action: "start"
      },
    });
  }, []);

  function updateStateAddress(id: number) {
    const stateSelected = usStates.find((opt) => opt.id === id)?.value;
    dispatch(update({...formState, state: stateSelected}));
  }

  return (
    <>
      <Container fluid className="personal-data-container">
        <Row className="ps-2 mb-3">
            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
              <TextInput
                name="firstName"
                tabIndex={1}
                maxLength={50}
                labelText={
                isPrimaryAccountHolder
                  ? t("transfer_phone.phoneForm.firstNameLabel")!
                  : t("transfer_phone.phoneForm.firstNameLabelOtherNonHolder")!
                }
                placeholder={t("transfer_phone.phoneForm.firstNamePlaceholder")!}
                width="529px"
                onChange={(val) => {
                dispatch(update({ ...formState, firstName: val }));
                }}
              />
            </Col>

            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
              <TextInput
                name="lastName"
                tabIndex={2}
                maxLength={50}
                labelText={
                isPrimaryAccountHolder
                  ? t("transfer_phone.phoneForm.lastNameLabel")!
                  : t("transfer_phone.phoneForm.lastNameLabelOtherNonHolder")!
                }
                placeholder={t("transfer_phone.phoneForm.lastNamePlaceholder")!}
                width="529px"
                onChange={(val) => {
                dispatch(update({ ...formState, lastName: val }));
                }}
              />
            </Col>
        </Row>

        <Row className="ps-2">
            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
            <Row>
              <Col>
                <TextInput
                  name="billingAddress"
                  tabIndex={3}
                  maxLength={50}
                  labelText={
                  isPrimaryAccountHolder
                    ? t("transfer_phone.phoneForm.billingAddressLabel")!
                    : t("transfer_phone.phoneForm.billingAddressLabelNonHolder")!
                  }
                  placeholder={t("transfer_phone.phoneForm.billingAddressPlaceholder")!}
                  width="529px"
                  onChange={(val) => {
                  dispatch(update({ ...formState, billingAddress: val }));
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col className="address-reminder">
                <strong>{t("transfer_phone.phoneForm.important")}</strong>{" "}
                {t("transfer_phone.phoneForm.addressReminder")}
              </Col>
            </Row>
            </Col>
            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
              <TextInput
                name="billingAddress2"
                tabIndex={4}
                maxLength={50}
                labelText={t("transfer_phone.phoneForm.billingAddress2Label")!}
                placeholder={t("transfer_phone.phoneForm.billingAddress2Placeholder")!}
                width="529px"
                onChange={(val) => {
                  dispatch(update({ ...formState, billingAddress2: val }));
                }}
              />
            </Col>
        </Row>

        <Row className="ps-2 mb-3">
            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
                <TextInput
                  name="city"
                  tabIndex={5}
                  maxLength={50}
                  labelText={t("transfer_phone.phoneForm.cityLabel")!}
                  placeholder={t("transfer_phone.phoneForm.cityPlaceholder")!}
                  width="529px"
                  onChange={(val) => {
                  dispatch(update({ ...formState, city: val }));
                  }}
                />
            </Col>

          <Col xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
            <Row>
              <Col className="state-label" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
                  <Dropdown
                    name="state"
                    tabIndex={6}
                    label={t("transfer_phone.phoneForm.stateLabel")!}
                    boldLabel={true}
                    headerPlaceholder={t("transfer_phone.phoneForm.statePlaceholder")!}
                    options={usStates}
                    onChange={(id: number) => updateStateAddress(id)}
                    width="105px"
                    height="40px"
                  />
              </Col>
              <Col className="pt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
                  <TextInput
                    name="zip"
                    tabIndex={7}
                    maxLength={10}
                    labelText={t("transfer_phone.phoneForm.zipCodeLabel")!}
                    placeholder={t("transfer_phone.phoneForm.zipCodePlaceholder")!}
                    width="129px"
                    normalize={zipMask}
                    onChange={(val) => {
                      dispatch(update({ ...formState, zip: val }));
                    }}
                  />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="ps-2 mb-3">
          <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
            <TextInput
              name="accountNumber"
              tabIndex={8}
              maxLength={50}
              labelText={t("transfer_phone.phoneForm.accountNumberLabel")!}
              placeholder={t("transfer_phone.phoneForm.accountNumberPlaceholder")!}
              width="400px"
              onChange={(val: String) => {
                dispatch(update({ ...formState, accountNumber: val }));
              }}
            />
          </Col>
        </Row>

        <Row className="ps-2">
            <Col className="mt-1" xs="auto" sm="auto" md="auto" lg="auto" xl="auto" xxl="auto">
            <Row>
              <Col className="pe-0">
              <Form.Label className="fw-bold mt-1 text-input-label">
                {t("transfer_phone.phoneForm.accountTransferPINLabel")}
              </Form.Label>
              <button
                type="button"
                tabIndex={9}
                ref={anchorRef}
                className="popper-button popper-info-button"
                style={{ marginBottom: "5px", marginLeft: "5px" }}
                onClick={handleButtonClick}
              >
                <InfoLogo />
              </button>
              </Col>
            </Row>

            <Popper
              id={id}
              open={open}
              anchorEl={anchorEl}
              placement="top-end"
              disablePortal
            >
              <div className="popper-content">
              <div className="popper-header">
                <button type="button" className="close-button" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="popper-text">
                {t("transfer_phone.popoverTransferPin")}
              </div>
              </div>
            </Popper>

            <TextInput
              name="accountPIN"
              tabIndex={10}
              maxLength={50}
              placeholder={t("transfer_phone.phoneForm.accountTransferPINPlaceholder")!}
              width="400px"
              onChange={(val) => {
              dispatch(update({ ...formState, accountPIN: val }));
              }}
            />
            </Col>
        </Row>

        <Row className="ps-2 my-5">
          <Col>
            <Button
              type="submit"
              tabIndex={11}
              className="transfer-phone-form-button"
              style={{ height: "49px", width: "231px" }}
              disabled={!canContinue}
            >
              {t("transfer_phone.reviewYourRequest")!}
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
};
